import React from 'react';
import './PrivacyPolicy.css';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-white text-gray-800 min-h-screen privacy-policy-container flex flex-col">
      <div className="container mx-auto px-4 py-8 max-w-4xl flex-grow">
        <header className="mb-8 flex justify-between items-center">
          <Link to="/" className="text-primary hover:text-accent flex items-center text-sm font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Syentist
          </Link>
          <div className="text-sm text-gray-500">Privacy</div>
        </header>

        <h1 className="text-3xl font-bold mb-8 text-center">Privacy Policy</h1>

        <div className="space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-3">Introduction</h2>
            <p className="mb-2">
              Syentist Lab Inc. ("we," "our," or "us") respects your privacy and describes here how we handle information when you visit <strong>https://syentist.com</strong> and associated pages operated by Syentist (the "Site"). By using the Site you agree to this Privacy Policy alongside any applicable Terms or notices we provide.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Information We Collect</h2>
            <p className="mb-2">We may collect the following categories of information in connection with the Site:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Communications:</strong> If you email us or use links on the Site that open your mail client, we receive your message contents and addressing information (such as email address).</li>
              <li><strong>Usage data:</strong> Information about how the Site is used, including pages viewed and referring addresses, timestamps, device type and browser/language where available.</li>
              <li><strong>Cookies &amp; analytics:</strong> We may use cookies or similar technologies, and embedded analytics providers, to understand traffic and Site performance.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">How We Use Your Information</h2>
            <p className="mb-2">We use information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Operate, secure, troubleshoot, and improve the Site;</li>
              <li>Respond to inquiries submitted to us;</li>
              <li>Monitor aggregate usage metrics and diagnose technical problems;</li>
              <li>Comply with law and enforce our legal rights;</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Sharing Your Information</h2>
            <p className="mb-2">We may disclose information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service providers:</strong> Vendors assisting us with hosting, analytics, communications, security, or business operations;</li>
              <li><strong>Legal matters:</strong> When required by law, regulation, subpoena, or to protect the rights or safety of Syentist, Site users, or others.</li>
            </ul>
            <p className="mt-2">
              We do not sell your personal information for marketing purposes as that term is commonly understood under U.S. state privacy statutes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Data Security</h2>
            <p>
              We maintain reasonable safeguards designed to protect information we collect. Transmission over the internet is never completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Your Choices</h2>
            <p className="mb-2">Depending on jurisdiction you may request access, deletion, correction, or other rights regarding certain personal information. You can also decline certain cookies via browser controls. Reach us via the contacts below.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Children&apos;s Privacy</h2>
            <p>
              The Site is not directed at individuals under 13, and we do not knowingly solicit personal information from children under that age without appropriate consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Changes to Our Privacy Policy</h2>
            <p>
              We may update this Privacy Policy by posting revisions on this page with a new "Last updated" date below. Continuing to use the Site after changes constitutes your acceptance unless otherwise prohibited by applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Syentist — business disclosure</h2>
            <p className="mb-2">
              Syentist Lab Inc. ("Syentist," "we") provides the following summary for business and counterpart identification.
            </p>
            <p className="mb-2">
              <strong>Services:</strong> Software consulting together with venture-studio-style development of micro-SaaS and related digital products, including discovery, product design and software engineering, experimentation and market validation for niche offerings, and related activities we use to incubate and scale those products.
            </p>
            <p className="mb-2">
              <strong>Industry classification (NAICS):</strong> 541514 — Computer Systems Design and Related Services.
            </p>
            <p className="mb-0">
              <strong>Customer engagement:</strong>{' '}
              <a
                href="https://www.linkedin.com/company/syentist"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-accent underline"
              >
                Syentist on LinkedIn
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Contact Information</h2>
            <p>
              Questions about this Privacy Policy or our privacy practices may be directed to:
            </p>
            <div className="mt-2 contact-info">
              <p><strong>Syentist Lab Inc.</strong></p>
              <p>Email: privacy@syentist.com</p>
              <p>Website: https://syentist.com</p>
            </div>
          </section>

          <section className="mt-8 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 last-updated">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
